import router from "@/router";
import axios from "axios";
import { EventHelper } from "@/helpers/eventHelper";
import { SUBSCRIPTION_STATE } from "@/models/subscription";

const faktoolSessionTokenKey = "faktool-session-token";
const faktoolReloginTokenKey = "faktool-relogin-token";
let reloginRequest;

class SessionHelpers {
  async login(email, password, rememberMe = false) {
    if (email && email !== "" && password && password !== "") {
      const encodedPassword = btoa(("fjhdhsTG7fmelklskf9" + password + "pol,wsmdWGOnbw7uj").split("").reverse().join(""));
      const response = await axios.post(`${import.meta.env.VITE_FAKTOOL_API_URL}/Authentication`, {
        email,
        password: encodedPassword
      });

      if (response.status === 200 && response.data) {
        sessionStorage.setItem(faktoolSessionTokenKey, response.data.token);
        if (rememberMe) {
          localStorage.setItem(faktoolReloginTokenKey, response.data.reloginToken);
        } else {
          sessionStorage.setItem(faktoolReloginTokenKey, response.data.reloginToken);
        }

        EventHelper.emit("userSessionChanged", this.amIAuthenticated);
        return true;
      } else {
        EventHelper.emit("userSessionChanged", this.amIAuthenticated);
        return false;
      }
    }
    EventHelper.emit("userSessionChanged", this.amIAuthenticated);
    return false;
  }

  async relogin() {
    if (reloginRequest) {
      return reloginRequest;
    }

    // eslint-disable-next-line no-async-promise-executor
    reloginRequest = new Promise(async resolve => {
      const reloginTokenFromLocalStorage = localStorage.getItem(faktoolReloginTokenKey);
      const reloginTokenFromSessionStorage = sessionStorage.getItem(faktoolReloginTokenKey);

      if (reloginTokenFromLocalStorage == null && reloginTokenFromSessionStorage == null) {
        this.logout();
        resolve(false);
        return;
      }

      try {
        const response = await axios.post(`${import.meta.env.VITE_FAKTOOL_API_URL}/Authentication/relogin`, {
          reloginToken: reloginTokenFromLocalStorage ? reloginTokenFromLocalStorage : reloginTokenFromSessionStorage
        });

        if (response.status === 200 && response.data) {
          sessionStorage.setItem(faktoolSessionTokenKey, response.data.token);

          if (reloginTokenFromLocalStorage) {
            localStorage.setItem(faktoolReloginTokenKey, response.data.reloginToken);
          } else {
            sessionStorage.setItem(faktoolReloginTokenKey, response.data.reloginToken);
          }

          EventHelper.emit("userSessionChanged", this.amIAuthenticated);
          resolve(true);
        } else {
          this.logout();
          resolve(false);
        }
      } catch (error) {
        switch (error.message) {
          case "Network Error":
            // Handle network error
            break;

          default:
            // Handle error
            break;
        }
        // Handle error
        this.logout();
        resolve(false);
      }
    });

    reloginRequest.finally(() => {
      reloginRequest = null;
    });

    return reloginRequest;
  }

  async loginFromOneTimeLoginToken(token) {
    try {
      const response = await axios.post(`${import.meta.env.VITE_FAKTOOL_API_URL}/Authentication/onetimelogin`, {
        reloginToken: token
      });
      if (response.status === 200 && response.data) {
        sessionStorage.setItem(faktoolSessionTokenKey, response.data.token);
        EventHelper.emit("userSessionChanged", this.amIAuthenticated);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  logout() {
    sessionStorage.removeItem(faktoolReloginTokenKey);
    sessionStorage.removeItem(faktoolSessionTokenKey);
    localStorage.removeItem(faktoolReloginTokenKey);
    localStorage.removeItem(faktoolSessionTokenKey);
    EventHelper.emit("userSessionChanged", this.amIAuthenticated);

    if (router.currentRoute.value?.meta?.authenticationNeeded) {
      router.push({ name: "login" });
    }
  }

  get accessToken() {
    return sessionStorage.getItem(faktoolSessionTokenKey);
  }

  get amIAuthenticated() {
    return sessionStorage.getItem(faktoolSessionTokenKey) !== null;
  }

  get isSubscriptionReadonly() {
    const sessionVars = parseJwt(this.accessToken);
    if (sessionVars?.subst != SUBSCRIPTION_STATE.READONLY && sessionVars?.subst != SUBSCRIPTION_STATE.EXPIRED) {
      return false;
    }
    return true;
  }

  get isSubscriptionExpired() {
    const sessionVars = parseJwt(this.accessToken);
    if (sessionVars?.subst != SUBSCRIPTION_STATE.EXPIRED) {
      return false;
    }
    return true;
  }

  get userId() {
    const sessionVars = parseJwt(this.accessToken);
    return sessionVars.subId || null;
  }
}

function parseJwt(token) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export const SessionHelper = new SessionHelpers();
